export function getRemediationText(module, title, additionalData) {
  const mod = (module || '').toLowerCase();
  const t = (title || '').toLowerCase();
  const steps = (...items) => items.map((item, index) => `${index + 1}. ${item}`).join('\n');

  if (mod.startsWith('breach-') || mod.includes('hibp') || mod.includes('dehashed')) {
    if (additionalData?.credentialType === 'plaintext') {
      return steps(
        'Identify every system where the exposed password may have been reused.',
        'Force a password reset for the affected account and revoke active sessions.',
        'Confirm MFA is enabled and conditional access policies cover the identity.',
        'Review sign-in logs for successful or suspicious access after the breach date.'
      );
    }
    const hash = (additionalData?.hashType || '').toLowerCase();
    if (['md5', 'sha1', 'ntlm'].includes(hash)) {
      return steps(
        `Treat the exposed ${additionalData.hashType} hash as recoverable credential material.`,
        'Force a password reset for the affected account and invalidate active sessions.',
        'Check whether the same password was reused across VPN, M365, AD, SaaS, or privileged systems.',
        'Validate the account has MFA and no risky mailbox, OAuth, or forwarding rules remain.'
      );
    }
    return steps(
      'Confirm whether the exposed address still belongs to an active user, alias, shared mailbox, or service account.',
      'Rotate credentials for the affected identity and any systems where reuse is plausible.',
      'Enable or verify MFA and block legacy authentication paths.',
      'Document the breach source, remediation date, and any residual client risk.'
    );
  }

  if (mod.includes('shodan') || (mod.includes('osint') && t.includes('exposed'))) {
    return steps(
      'Confirm the exposed service, port, protocol, and owning business system.',
      'Close the port or restrict access with firewall, VPN, or source IP allow-listing.',
      'Patch and harden the service if it must remain reachable.',
      'Re-query the exposure source and re-scan the asset to confirm the public path is gone.'
    );
  }

  if (mod.includes('dns') || mod.includes('osint-dns')) {
    if (t.includes('spf')) {
      return steps(
        'Inventory authorized mail senders and update the SPF include/ip mechanisms.',
        'Change the terminal SPF mechanism from soft-fail to hard-fail once legitimate senders are covered.',
        'Send test mail through each approved sender and confirm SPF alignment passes.',
        'Monitor mail flow for rejected legitimate messages before closing the finding.'
      );
    }
    if (t.includes('dnssec')) {
      return steps(
        'Confirm the authoritative DNS provider and registrar support DNSSEC for the domain.',
        'Generate/sign the zone and publish the DS record at the registrar.',
        'Validate the DNSSEC chain with an external resolver test.',
        'Monitor renewals and key rollover dates so DNSSEC does not expire silently.'
      );
    }
    if (t.includes('dmarc')) {
      return steps(
        'Publish or review the DMARC record with reporting addresses.',
        'Confirm SPF and DKIM alignment for all legitimate mail sources.',
        'Move from p=none to quarantine or reject after reports show legitimate mail is aligned.',
        'Continue monitoring aggregate reports for spoofing attempts and broken senders.'
      );
    }
    if (t.includes('zone transfer')) {
      return steps(
        'Identify the authoritative DNS server allowing zone transfer.',
        'Restrict AXFR/IXFR to approved secondary nameserver IP addresses only.',
        'Block zone transfer at the firewall if public DNS infrastructure allows it.',
        'Retest from an external network and preserve evidence that transfer is denied.'
      );
    }
    if (t.includes('wildcard')) {
      return steps(
        'Confirm whether the wildcard record supports a current production workflow.',
        'If unnecessary, remove the wildcard or replace it with explicit host records.',
        'If required, document the owner and expected scope of the wildcard.',
        'Retest domain enumeration and confirm unexpected hosts no longer resolve.'
      );
    }
    return steps(
      'Identify the DNS zone, record, provider, and business owner.',
      'Apply the recommended hardening change for the misconfiguration.',
      'Validate externally using independent DNS resolvers.',
      'Document the final record value and close only after propagation is complete.'
    );
  }

  if (mod.startsWith('dast') || mod.includes('zap') || mod.includes('nuclei')) {
    const solution = additionalData?.solution ?? additionalData?.remediation;
    if (solution) return solution;
    return steps(
      'Confirm the affected URL, method, parameter, payload, and response evidence.',
      'Fix the vulnerable code path using the OWASP guidance for the finding class.',
      'Add a regression test or security control so the issue is not reintroduced.',
      'Re-run the same DAST check and confirm adjacent routes or parameters are not affected.'
    );
  }

  if (mod.includes('certspotter') || mod.includes('certificate')) {
    return steps(
      'Confirm the certificate common name, SANs, issuer, expiration, and owning system.',
      'Renew or replace expired/weak certificates before service impact occurs.',
      'Revoke unauthorized certificates and investigate how issuance occurred.',
      'Validate the public certificate chain and update the asset inventory.'
    );
  }

  if (mod.startsWith('ad-') || mod === 'ad-security') {
    return steps(
      'Identify the affected AD object, group, ACL, trust, session, or certificate template.',
      'Confirm whether the permission or relationship is required for business operations.',
      'Remove, constrain, or monitor the risky permission path using least privilege.',
      'Re-run the AD assessment and confirm the same path no longer appears.'
    );
  }

  if (mod.includes('cloud') || mod.includes('aws') || mod.includes('azure') || mod.includes('cspm')) {
    return steps(
      'Identify the affected subscription/account, region, resource, and policy control.',
      'Validate whether the configuration is intentional and whether compensating controls exist.',
      'Apply the CIS/provider-recommended remediation using least privilege and logging defaults.',
      'Run cloud connection verification and a follow-up scan to confirm the finding is cleared.'
    );
  }

  if (mod.startsWith('dast') || mod.includes('zap') || mod.includes('web-scan') || mod.includes('nuclei')) {
    if (t.includes('sql injection') || t.includes('sqli')) {
      return steps(
        'Identify the vulnerable query, endpoint, parameter, and reachable database path.',
        'Replace string-built SQL with parameterized queries or prepared statements.',
        'Validate and constrain user input before it reaches the data layer.',
        'Retest with the original payload and confirm database errors or extracted data are no longer returned.'
      );
    }
    if (t.includes('cross-site scripting') || t.includes('xss')) {
      return steps(
        'Identify the reflected, stored, or DOM-based injection point.',
        'Encode output for the correct HTML, attribute, JavaScript, or URL context.',
        'Validate and sanitize input at the boundary and add a restrictive Content-Security-Policy where feasible.',
        'Retest with the original payload and confirm no executable script is rendered.'
      );
    }
    if (t.includes('csrf') || t.includes('cross-site request forgery')) {
      return steps(
        'Inventory state-changing routes that accept browser-authenticated requests.',
        'Require anti-CSRF tokens or equivalent origin checks on those routes.',
        'Set session cookies to SameSite=Lax or Strict where compatible.',
        'Retest the original proof of concept and confirm unauthorized cross-origin submission fails.'
      );
    }
    if (t.includes('header') || t.includes('x-frame') || t.includes('content-type') || t.includes('hsts')) {
      return steps(
        'Identify the missing or weak response header and all affected routes.',
        'Configure the application, proxy, CDN, or web server to emit the recommended value.',
        'Confirm the header does not break expected application behavior.',
        'Retest with DAST and a browser/devtools check to confirm consistent coverage.'
      );
    }
    if (t.includes('cookie') || t.includes('secure flag') || t.includes('httponly')) {
      return steps(
        'Identify all cookies missing Secure, HttpOnly, or SameSite attributes.',
        'Set Secure on cookies transmitted over HTTPS and HttpOnly on session or auth cookies.',
        'Set SameSite to Lax or Strict unless a documented cross-site flow requires otherwise.',
        'Retest authentication and DAST cookie checks after deployment.'
      );
    }
    return steps(
      'Confirm the affected endpoint, request shape, and scanner evidence.',
      'Map the finding to the relevant OWASP category and identify the root cause.',
      'Apply the code, configuration, or control change required for that weakness.',
      'Retest the endpoint and document residual risk if the issue cannot be fully removed.'
    );
  }

  return steps(
    'Confirm the source module, affected asset, owner, and evidence for this finding.',
    'Determine whether the issue is exploitable, externally reachable, or tied to sensitive data.',
    'Apply the recommended patch, configuration change, permission change, or compensating control.',
    'Validate with a follow-up scan and document the remediation date, evidence, and residual risk.'
  );
}
